import React, { useMemo, useState } from "react";
import { Table } from "@mantine/core";

type Header<T> = {
  key: keyof T;
  label: string;
  sortable?: boolean;
};

type Props<T extends Record<string, any>> = {
  headers: Header<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  onWinnerClick?: (row: T) => void;
  isWinner?: (row: T) => boolean;
};

type ThProps = {
  children: React.ReactNode;
  sorted?: boolean;
  reversed?: boolean;
  onClick?: () => void;
};

function Th({ children, sorted, reversed, onClick }: ThProps) {
  return (
    <th
      onClick={onClick}
      style={{
        cursor: onClick ? "pointer" : "default",
        userSelect: "none",
        padding: "8px",
      }}
      aria-sort={sorted ? (reversed ? "descending" : "ascending") : undefined}
    >
      <span
        style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
      >
        {children}
        {sorted !== undefined && <span>{reversed ? "🔽" : "🔼"}</span>}
      </span>
    </th>
  );
}

export function GenericTable<T extends Record<string, any>>({
  headers,
  data,
  onRowClick,
  onWinnerClick,
  isWinner,
}: Props<T>) {
  const [sortBy, setSortBy] = useState<keyof T | null>(null);
  const [reversed, setReversed] = useState(false);

  const sortedData = useMemo(() => {
    if (!sortBy) return data;
    return [...data].sort((a, b) => {
      const aVal = String(a[sortBy]);
      const bVal = String(b[sortBy]);
      return reversed ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
    });
  }, [data, sortBy, reversed]);

  const handleSort = (key: keyof T) => {
    if (key === sortBy) {
      setReversed((prev) => !prev);
    } else {
      setSortBy(key);
      setReversed(false);
    }
  };

  return (
    <Table highlightOnHover>
      <thead>
        <tr>
          {headers.map((header) => (
            <Th
              key={String(header.key)}
              sorted={sortBy === header.key}
              reversed={reversed}
              onClick={
                header.sortable ? () => handleSort(header.key) : undefined
              }
            >
              {header.label}
            </Th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedData.map((row, index) => (
          <tr key={index}>
            {headers.map((header, colIndex) => (
              <td
                key={String(header.key)}
                style={{
                  cursor: colIndex === 0 && onRowClick ? "pointer" : "default",
                  color:
                    String(row[header.key]) === "Ex aequo"
                      ? "#FF0000"
                      : colIndex === 0 && onRowClick
                        ? "#0000EE"
                        : "inherit",
                }}
                onClick={
                  colIndex === 0 && onRowClick
                    ? () => onRowClick(row)
                    : undefined
                }
              >
                {header.key === "winner" ? (
                  <div className="flex justify-center">
                    <input
                      type="checkbox"
                      checked={isWinner?.(row) || false} // Add this
                      onChange={() => onWinnerClick?.(row)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                ) : /<[^>]*>/.test(String(row[header.key])) ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: String(row[header.key]),
                    }}
                  />
                ) : (
                  String(row[header.key])
                    .split(/(?=#\d)/g)
                    .map((part, i) => (
                      <React.Fragment key={i}>
                        {part}
                        <br />
                      </React.Fragment>
                    ))
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
